import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ValidooExportErrorInfo } from './types';
import ValidooExportCheckCard from './ValidooExportCheckCard';

interface ValidooExportBlockedDialogProps {
  open: boolean;
  errorInfo: ValidooExportErrorInfo | null;
  onClose: () => void;
}

function BlockedContent({ errorInfo }: { errorInfo: ValidooExportErrorInfo & { kind: 'blocked' } }) {
  const preflight = errorInfo.data.preflight;
  const checks = preflight?.checks || [];
  const failedCount = checks.filter((check) => check.severity === 'blocking' && check.status !== 'pass').length;
  const passedCount = checks.filter((check) => check.status === 'pass').length;
  const errorCount = checks.filter((check) => check.status === 'error').length;
  const assetTitle = preflight.asset?.fileName || 'Validoo-export';
  const assetSubtitle = preflight.resolvedData?.gtin?.normalizedForFilename
    ? `GTIN ${preflight.resolvedData.gtin.normalizedForFilename}`
    : preflight.resolvedData?.gtin?.value
      ? `GTIN ${preflight.resolvedData.gtin.value}`
      : `Asset-ID ${preflight.assetId}`;
  const metaParts = [
    preflight.asset?.width && preflight.asset?.height
      ? `Dimensioner: ${preflight.asset.width} x ${preflight.asset.height} px`
      : '',
    preflight.resolvedData?.icc?.sourceProfileName
      ? `ICC: ${preflight.resolvedData.icc.sourceProfileName}`
      : '',
    preflight.resolvedData?.icc?.sourceColorSpace
      ? `Färgrymd: ${preflight.resolvedData.icc.sourceColorSpace}`
      : '',
    preflight.resolvedData?.clippingPath?.name
      ? `Urklippsbana: ${preflight.resolvedData.clippingPath.name}`
      : '',
    preflight.targets?.marketImage?.filename ? `Marknadsbild: ${preflight.targets.marketImage.filename}` : '',
    preflight.targets?.planogramImage?.filename ? `Planogram: ${preflight.targets.planogramImage.filename}` : '',
  ].filter(Boolean);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: 2,
          p: 2,
          borderRadius: 2,
          backgroundColor: errorCount > 0 ? '#fff3e0' : '#fce4ec',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: errorCount > 0 ? '#ed6c02' : '#c62828' }}>
            Bilden är underkänd
          </Typography>
          <Typography variant="body2" color="text.secondary">
            F&ouml;ljande krav m&aring;ste uppfyllas f&ouml;r att Validoo-bilderna ska kunna skapas.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip
            icon={<WarningAmberIcon />}
            label={`${passedCount} godkända`}
            size="small"
            sx={{ backgroundColor: '#c8e6c9', fontWeight: 600 }}
          />
          <Chip
            icon={<CancelIcon />}
            label={`${failedCount} underkända`}
            size="small"
            sx={{ backgroundColor: '#ffcdd2', fontWeight: 600 }}
          />
          {errorCount > 0 && (
            <Chip
              icon={<WarningAmberIcon />}
              label={`${errorCount} fel`}
              size="small"
              sx={{ backgroundColor: '#ffe0b2', fontWeight: 600 }}
            />
          )}
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#072f53' }}>
          Resultat (1 bild)
        </Typography>
      </Box>

      <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
        <ValidooExportCheckCard
          title={assetTitle}
          subtitle={assetSubtitle}
          checks={checks}
          metaLine={metaParts.join(' • ')}
        />
      </Box>
    </>
  );
}

function GenericErrorContent({ message }: { message: string }) {
  return (
    <Box sx={{ py: 1 }}>
      <Alert severity="error" icon={<ErrorOutlineIcon fontSize="inherit" />}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          N&aring;got gick fel
        </Typography>
        <Typography variant="body2">{message}</Typography>
      </Alert>
    </Box>
  );
}

const ValidooExportBlockedDialog: React.FC<ValidooExportBlockedDialogProps> = ({
  open,
  errorInfo,
  onClose,
}) => {
  const isBlocked = errorInfo?.kind === 'blocked';
  const titleText = isBlocked ? 'Spara för Validoo' : 'Spara för Validoo';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isBlocked ? (
            <CancelIcon sx={{ color: '#d32f2f', fontSize: 28 }} />
          ) : (
            <ErrorOutlineIcon sx={{ color: '#ed6c02', fontSize: 28 }} />
          )}
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {titleText}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 2.5 }}>
        {errorInfo?.kind === 'blocked' ? (
          <BlockedContent errorInfo={errorInfo} />
        ) : errorInfo?.kind === 'error' ? (
          <GenericErrorContent message={errorInfo.message} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Ingen information tillg&auml;nglig.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ backgroundColor: '#072f53', '&:hover': { backgroundColor: '#0a3d6b' } }}
        >
          St&auml;ng
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidooExportBlockedDialog;
